import React from "react";
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import RequiredLabel from "../../../../../common/required-label/RequiredLabel";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import styles from "./ReferralCode.module.scss";
import { colors } from "../../../../../../constants/colors";
import { sanitizeDataTypeClientSide } from "../../../../../../utils/utils";
import { useDispatch } from "react-redux";
import {
  updateCustomerReferralCodeAction,
  updateServiceProviderReferralCodeAction,
} from "../../../../../../redux/services/referral-codes/referralCodesServices";
import { InfoButtonIcon } from "../../../../../../assets/svg";
import Title from "../../../../../common/customTitle/CustomTitle";
import { routes } from "../../../../../../constants";
import { baseRouteMakerForSuperAdmin } from "../../../../../../constants/routeConsts";
import { useNavigate } from "react-router-dom";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

const ReferralCode = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch dependencies */
  const dispatch = useDispatch();
  /* dispatch dependencies */

  const navigate = useNavigate();

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* form dependencies */
  const [form] = Form.useForm();
  const initialValues = {
    type: null,
    reward_for_referrer_amount: "",
    reward_for_referee_amount: "",
    max_referee_allowed: "",
    reward_after_threshold_reached: "",
    referral_program_is_active:false
  };
  const [status, setStatus] = useState(false);
  /* form dependencies */

  /* on Category changed */
  const onCategoryChanged = () => {};
  /* on Category changed */

  /* reset default form fields */
  const resetDefaultFields = () => {
    form.setFieldsValue({
      ...initialValues,
    });
  };
  /* reset default form fields */


  const handleViewList = () => {
    navigate(baseRouteMakerForSuperAdmin(routes.REFERRAL_CODE_LIST))
  }

  /* on status changed */
  const onStatusChanged = (value) => {
    setStatus(value)
  }
  /* on status changed */


  /* on submit */
  const onSubmit = (values) => {
    const {referral_program_is_active, ...rest} = values;
    const dataToBeSent = {
      referral_program_is_active: (referral_program_is_active === "" || referral_program_is_active === undefined || referral_program_is_active === null) ? false : true,
      ...rest,
    };

    switch (values?.type) {
      case "service_provider":
        dispatch(
          updateServiceProviderReferralCodeAction(setLoading, dataToBeSent)
        ).then(() => {
          resetDefaultFields();
        });
        break;

      case "customer":
        dispatch(
          updateCustomerReferralCodeAction(setLoading, dataToBeSent)
        ).then(() => {
          resetDefaultFields();
        });
        break;

      default:
        break;
    }
  };

  return (
    <div className={styles.referralCodeContainer}>
      <Flex vertical gap="2rem">
        <Flex justify="space-between" align="center">
        <Title level={2}>{t("Modules.Configuration.referralCode")}</Title>
          <Button
              type="primary"
              loading={loading}
              disabled={loading}
              style={{ marginTop: "10px" }}
              onClick={(e) => handleViewList(e)}
            >
              {t("CommonUtils.logs")}
            </Button>
        </Flex>
          
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          requiredMark={false}
          onFinish={onSubmit}
        >
          <Row gutter={[24, 24]}>
            <Col span={8}>
              <Form.Item
                name="type"
                label={
                  <RequiredLabel
                    text={t("Modules.Configuration.userType")}
                    required={true}
                  />
                }
                rules={[
                  {
                    required: true,
                    message: t("Modules.Configuration.pleaseSelectUserType"),
                  },
                ]}
              >
                <Select
                  loading={selectListLoader}
                  options={[
                    { value: "service_provider", label: "Service Provider" },
                    { value: "customer", label: "User" },
                  ]}
                  placeholder={t("Modules.Configuration.selectUserType")}
                  onChange={onCategoryChanged}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="reward_for_referee_amount"
                label={
                  <Flex gap="1rem" align="center" justify="center">
                    <RequiredLabel
                      text={t("Modules.ReferralCode.refereePointsCredit")}
                      required={true}
                    />

                    <Tooltip
                      placement="rightTop"
                      title={t(
                        "Modules.ReferralCode.refereeCreditPointsTooltipInfo"
                      )}
                      color={colors.DarkMint}
                    >
                      <Button type="text" shape="circle" size="small">
                        <Flex align="center" justify="center">
                          <InfoButtonIcon />
                        </Flex>
                      </Button>
                    </Tooltip>
                  </Flex>
                }
                rules={[
                  {
                    required: true,
                    message: t("Modules.ReferralCode.pleaseEnterPointsCredit"),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  type="number"
                  controls={false}
                  placeholder={t("Modules.ReferralCode.enterPointsCredit")}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="reward_for_referrer_amount"
                label={
                  <Flex gap="1rem" align="center" justify="center">
                    <RequiredLabel
                      text={t("Modules.ReferralCode.referrerPointsCredit")}
                      required={true}
                    />

                    <Tooltip
                      placement="rightTop"
                      title={t(
                        "Modules.ReferralCode.referrerCreditPointsTooltipInfo"
                      )}
                      color={colors.DarkMint}
                    >
                      <Button type="text" shape="circle" size="small">
                        <Flex align="center" justify="center">
                          <InfoButtonIcon />
                        </Flex>
                      </Button>
                    </Tooltip>
                  </Flex>
                }
                rules={[
                  {
                    required: true,
                    message: t("Modules.ReferralCode.pleaseEnterPointsCredit"),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  type="number"
                  controls={false}
                  placeholder={t("Modules.ReferralCode.enterPointsCredit")}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="max_referee_allowed"
                label={
                  <RequiredLabel
                    text={t("Modules.ReferralCode.maxAllowedReferee")}
                    required={true}
                  />
                }
                rules={[
                  {
                    required: true,
                    message: t(
                      "Modules.ReferralCode.pleaseEnterMaxAllowedReferee"
                    ),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  type="number"
                  controls={false}
                  placeholder={t("Modules.ReferralCode.enterMaxAllowedReferee")}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="reward_after_threshold_reached"
                label={
                  <RequiredLabel
                    text={t("Modules.ReferralCode.rewardAfterThresholdReached")}
                    required={true}
                  />
                }
                rules={[
                  {
                    required: true,
                    message: t(
                      "Modules.ReferralCode.pleaseEnterRewardAfterThresholdReached"
                    ),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  type="number"
                  controls={false}
                  placeholder={t(
                    "Modules.ReferralCode.enterRewardAfterThresholdReached"
                  )}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="referral_program_is_active"
                label={
                  <RequiredLabel
                    text={t("reviewInfo.status")}
                    required={false}
                  />
                }
              >
                <Switch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  checked={status}
                  onClick={(e) => onStatusChanged(e)}
                />
              </Form.Item>
            </Col>
          </Row>

          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={loading}
            style={{ marginTop: "10px" }}
          >
            {t("CommonUtils.update")}
          </Button>
        </Form>
      </Flex>
    </div>
  );
};

export default ReferralCode;
